// Page entry. PRD 06 §1.2:
//   - PRO_MGMT:NO_PERMISSION → LNB still shows the menu (PRD 21 §4.5), entry is
//     blocked here with the 권한 없음 modal.
//   - READ / WRITE / Root → see the page; component-level write checks gate
//     the deploy + edit controls (added in later slices).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { DeployHeaderActions } from '@/features/pro-deployment/components/DeployHeaderActions';
import { DeployHistoryCard } from '@/features/pro-deployment/components/DeployHistoryCard';
import { DeploymentTabs, type DeploymentTab } from '@/features/pro-deployment/components/DeploymentTabs';
import { DiseaseScopeCard } from '@/features/pro-deployment/components/DiseaseScopeCard';
import { ModuleCardList } from '@/features/pro-deployment/components/ModuleCardList';
import { StatsRow } from '@/features/pro-deployment/components/StatsRow';
import { WeekCalendarTable } from '@/features/pro-deployment/components/WeekCalendarTable';
import {
  useDeploymentCurrentConfigQuery,
  useDiseaseOptionsQuery,
} from '@/features/pro-deployment/hooks/use-deployment-current-config';
import type { ProItemRef } from '@/features/pro-deployment/types/pro-deployment.types';
import { UpperLayerNotice } from '@/features/pro-deployment/components/UpperLayerNotice';
import { isViewOnlyOnRow, itemInView } from '@/features/pro-deployment/utils/disease-scope';
import { useRequirePermission } from '@/shared/hooks/useRequirePermission';

function LoadingCard() {
  const { t } = useTranslation('pro-deployment');
  return (
    <div
      className="card"
      role="status"
      aria-live="polite"
      style={{
        padding: 48,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <Loader2 size={32} color="var(--muted-foreground)" className="animate-spin" />
      <p style={{ margin: 0, color: 'var(--muted-foreground)' }}>{t('state.loading')}</p>
    </div>
  );
}

function ErrorCard({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('pro-deployment');
  return (
    <div
      className="card"
      role="alert"
      style={{
        padding: 48,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <AlertTriangle size={40} color="var(--destructive)" />
      <h2 style={{ margin: 0 }}>{t('state.errorTitle')}</h2>
      <p style={{ margin: 0, color: 'var(--muted-foreground)' }}>{t('state.errorMessage')}</p>
      <button type="button" className="btn btn-default btn-primary" onClick={onRetry}>
        {t('state.retry')}
      </button>
    </div>
  );
}

export function ProDeploymentPage() {
  const { t } = useTranslation('pro-deployment');
  // SHCT-171 — was a private in-page card. PRD 21 §7.2 specifies 권한 없음 as a 모달 with
  // "접근 권한이 없습니다.", so every gated page now blocks the same way through this hook.
  const canRead = useRequirePermission('PRO_MGMT', 'READ');
  // Selected disease row. Held in page state (not derived from the response) so it
  // survives the refetch that follows a disease change — the request is keyed off
  // THIS value. Default 전체/All (PRD 06 §2.2 기본값 전체). Choosing a row only
  // changes what is shown; it saves nothing and writes no audit row (FEATURE_025).
  const [disease, setDisease] = useState('ALL');
  const configQuery = useDeploymentCurrentConfigQuery({ disease });
  const optionsQuery = useDiseaseOptionsQuery();
  const [activeTab, setActiveTab] = useState<DeploymentTab>('FREQUENCY');

  // FEATURE_025 — every row shares one module set; the row decides which of its items are shown,
  // counted and numbered. Matched by id (the option carries groupId / codeId).
  const diseaseOption = useMemo(
    () => optionsQuery.data?.find((o) => o.value === disease),
    [optionsQuery.data, disease],
  );
  const isVisible = useMemo(
    () => (item: ProItemRef) => itemInView(item, disease, diseaseOption),
    [disease, diseaseOption],
  );
  // FEATURE_025 — on a code row, the common and group items it shows are 조회 전용 (PRD 06 v1.21 §3.1).
  const isViewOnly = useMemo(
    () => (item: ProItemRef) => isViewOnlyOnRow(item, diseaseOption),
    [diseaseOption],
  );

  if (!canRead) return null;

  const config = configQuery.data;
  const isInitialLoad = configQuery.isLoading && !config;

  return (
    <>
      <div className="content__header">
        <div>
          <h1 className="content__title">{t('title')}</h1>
        </div>
        {config ? (
          <DeployHeaderActions
            deploy={config.deploy}
            modules={config.modules}
            items={config.items}
            diseaseFilter={disease}
            diseaseOption={diseaseOption}
            isVisible={isVisible}
          />
        ) : null}
      </div>
      <div className="content__body">
        {isInitialLoad ? <LoadingCard /> : null}
        {configQuery.isError && !config ? (
          <ErrorCard onRetry={() => configQuery.refetch()} />
        ) : null}
        {config ? (
          <>
            <DiseaseScopeCard
              diseaseFilter={disease}
              monitoringWeeks={config.monitoringWeeks}
              onDiseaseChange={setDisease}
            />
            <UpperLayerNotice layers={config.deploy.undeployedUpperLayers} />
            <StatsRow
              modules={config.modules}
              items={config.items}
              monitoringWeeks={config.monitoringWeeks}
              isVisible={isVisible}
            />
            <DeploymentTabs activeTab={activeTab} onChange={setActiveTab} />
            {activeTab === 'FREQUENCY' ? (
              <ModuleCardList
                modules={config.modules}
                items={config.items}
                diseaseFilter={disease}
                isVisible={isVisible}
                isViewOnly={isViewOnly}
              />
            ) : null}
            {activeTab === 'CALENDAR' ? (
              <WeekCalendarTable
                modules={config.modules}
                items={config.items}
                monitoringWeeks={config.monitoringWeeks}
              />
            ) : null}
            <DeployHistoryCard />
          </>
        ) : null}
      </div>
    </>
  );
}
